import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { CommentsStatus, IComment, IPendingComment } from 'types/comments';

export interface ICommentsState {
  byImageId: Record<string, IComment[]>;
  statusByImageId: Record<string, CommentsStatus>;
  // Every image's count, fetched once for the whole collection so a tile can badge itself without
  // opening its thread. A thread that has been read outranks it — see selectCommentCountForImage.
  countsByImageId: Record<string, number>;
  countsStatus: CommentsStatus;
  savingImageId: string | null;
  deletingCommentId: string | null;
  identitySaving: boolean;
  // Draft held across the identity-modal round trip so the visitor doesn't retype it. Carries its
  // own imageId: the visitor can navigate to another photo while the modal is open.
  pendingComment: IPendingComment | null;
}

const initialState: ICommentsState = {
  byImageId: {},
  statusByImageId: {},
  countsByImageId: {},
  countsStatus: 'idle',
  savingImageId: null,
  deletingCommentId: null,
  identitySaving: false,
  pendingComment: null
};

const commentsSlice = createSlice({
  name: 'comments',
  initialState,
  reducers: {
    setCommentsStatus(
      state,
      { payload }: PayloadAction<{ imageId: string; status: CommentsStatus }>
    ) {
      state.statusByImageId[payload.imageId] = payload.status;
    },
    setImageComments(state, { payload }: PayloadAction<{ imageId: string; comments: IComment[] }>) {
      state.byImageId[payload.imageId] = payload.comments;
      state.statusByImageId[payload.imageId] = 'succeeded';
    },
    setCommentCountsStatus(state, { payload }: PayloadAction<CommentsStatus>) {
      state.countsStatus = payload;
    },
    setCommentCounts(state, { payload }: PayloadAction<Record<string, number>>) {
      state.countsByImageId = payload;
      state.countsStatus = 'succeeded';
    },
    addImageComment(state, { payload }: PayloadAction<{ imageId: string; comment: IComment }>) {
      const existing = state.byImageId[payload.imageId] || [];

      // The API sorts newest first; keep the cache in that order so the panel's single reverse
      // stays the only place ordering is decided.
      state.byImageId[payload.imageId] = [payload.comment, ...existing];
    },
    removeImageComment(state, { payload }: PayloadAction<{ imageId: string; commentId: string }>) {
      const existing = state.byImageId[payload.imageId];
      if (!existing) return;

      state.byImageId[payload.imageId] = existing.filter(
        (comment) => comment._id !== payload.commentId
      );
    },
    setCommentSaving(state, { payload }: PayloadAction<string | null>) {
      state.savingImageId = payload;
    },
    setCommentDeleting(state, { payload }: PayloadAction<string | null>) {
      state.deletingCommentId = payload;
    },
    setIdentitySaving(state, { payload }: PayloadAction<boolean>) {
      state.identitySaving = payload;
    },
    setPendingComment(state, { payload }: PayloadAction<IPendingComment | null>) {
      state.pendingComment = payload;
    },
    resetComments: () => initialState
  }
});

export const {
  setCommentsStatus,
  setImageComments,
  setCommentCounts,
  setCommentCountsStatus,
  addImageComment,
  removeImageComment,
  setCommentSaving,
  setCommentDeleting,
  setIdentitySaving,
  setPendingComment,
  resetComments
} = commentsSlice.actions;

export const commentsReducer = commentsSlice.reducer;
